import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, LogOut, Zap, Film, Crown, Building2,
  Layers, Edit2, Trash2, FileText, Book, Tv, Settings,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import LanguageSelector from '../components/LanguageSelector';
import UpgradeModal from '../components/UpgradeModal';
import UpgradeBanner from '../components/UpgradeBanner';
import RenewalBanner from '../components/RenewalBanner';
import WritingStreak from '../components/WritingStreak';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { useProject } from '../context/ProjectContext';
import { authFetch } from '../lib/authFetch';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_META = {
  spark:      { label: 'Spark',      icon: Zap,       color: 'text-muted-foreground', bg: 'bg-muted/20',      border: 'border-muted/30' },
  indie:      { label: 'Indie',      icon: Film,      color: 'text-primary',          bg: 'bg-primary/20',    border: 'border-primary/30' },
  studio:     { label: 'Studio',     icon: Building2, color: 'text-purple-400',       bg: 'bg-purple-400/20', border: 'border-purple-400/30' },
};

const FORMAT_LABELS = {
  feature:      'Feature Film',
  feature_film: 'Feature Film',
  short:        'Short Film',
  short_film:   'Short Film',
  novel:        'Novel',
  tv_episode:   'TV Episode',
  tv_pilot:     'Pilot',
  tv_series:    'TV Series',
  series:       'Series',
};

const FORMAT_ICONS = {
  feature:      Film,
  feature_film: Film,
  short:        FileText,
  short_film:   FileText,
  novel:        Book,
  tv_episode:   Tv,
  tv_pilot:     Tv,
  tv_series:    Tv,
  series:       Tv,
};

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { setActiveProject } = useProject();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, project: null });
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [renewal, setRenewal] = useState(null);

  useEffect(() => {
    setActiveProject(null);
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [userRes, projectsRes] = await Promise.all([
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' }),
      ]);
      const me = await userRes.json();
      setUser(me);
      setProjects(await projectsRes.json());
      if (me?.subscription_tier && me.subscription_tier !== 'spark') fetchRenewal();
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // A one-off mobile-money plan has an end date; the banner asks for a
  // renewal from RENEWAL_BANNER_DAYS before it. Never blocks the dashboard.
  const fetchRenewal = async () => {
    try {
      const res = await authFetch(`${API}/subscription/status`, { credentials: 'include' });
      if (!res.ok) return;
      const status = await res.json();
      setRenewal(status?.renewal ? { ...status.renewal, planName: status.plan_name } : null);
    } catch {}
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      Cookies.remove('session_token');
      navigate('/');
    } catch {
      toast.error('Logout failed');
    }
  };

  const handleCreateProject = () => {
    const tier = user?.subscription_tier || 'spark';
    const plotCount = projects.filter(p => p.project_type === 'plot').length;
    // Mirrors the server: Spark 1 plot/story project, Indie 3, Studio unlimited.
    const limit = tier === 'spark' ? 1 : tier === 'indie' ? 3 : null;
    if (limit !== null && plotCount >= limit) {
      setUpgradeModal({
        title: 'Ready for your next story?',
        description: tier === 'spark'
          ? 'Spark lets you build one project at a time. Upgrade to Indie to run up to 3 projects at once.'
          : 'Indie runs up to 3 projects at once. Upgrade to Studio for unlimited projects.',
        benefits: tier === 'spark'
          ? ['Up to 3 projects on Indie', 'Script editor — write, import, export', 'Unlimited AI Mentor coaching (300/mo)', 'Clean PDF exports']
          : ['Unlimited projects on Studio', 'Unlimited AI Mentor', 'Series Builder', 'Modular exports'],
        ctaText: tier === 'spark' ? 'Upgrade to Indie' : 'Upgrade to Studio',
      });
      return;
    }
    navigate('/new-project');
  };

  const handleProjectClick = (project) => {
    if (editingId) return;
    if (project.format === 'tv_series') {
      navigate(`/series/${project.project_id}`);
      return;
    }
    navigate(`/project/${project.project_id}`);
  };

  const handleDelete = (e, project) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, project });
  };

  const confirmDelete = async () => {
    try {
      await authFetch(`${API}/projects/${deleteModal.project.project_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      toast.success('Project deleted');
      setDeleteModal({ isOpen: false, project: null });
      fetchAll();
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const startEdit = (e, project) => {
    e.stopPropagation();
    setEditingId(project.project_id);
    setEditTitle(project.title);
  };

  const handleRename = async (e, projectId) => {
    e.stopPropagation();
    if (!editTitle.trim()) { toast.error('Title cannot be empty'); return; }
    try {
      await authFetch(`${API}/projects/${projectId}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: editTitle }),
      });
      toast.success('Project renamed');
      setEditingId(null);
      setEditTitle('');
      fetchAll();
    } catch {
      toast.error('Failed to rename project');
    }
  };

  const tier = user?.subscription_tier || 'spark';
  const tierMeta = TIER_META[tier] || TIER_META.spark;

  const plotProjects = [...(projects || [])]
    .filter(p => p.project_type === 'plot')
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <div className="flex flex-col">
        {/* Header */}
        <header className="glassmorphism sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-2">
            <h1 className="font-mono text-xl sm:text-2xl font-bold tracking-tighter flex-shrink-0">PANGAJI</h1>
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <LanguageSelector userTier={tier} />
              {(() => {
                const TierIcon = tierMeta.icon;
                return (
                  <div className={`hidden sm:flex items-center gap-2 ${tierMeta.bg} border ${tierMeta.border} px-3 py-1.5 flex-shrink-0`}>
                    <TierIcon className={`w-4 h-4 ${tierMeta.color}`} />
                    <span className={`text-xs font-mono uppercase ${tierMeta.color}`}>{tierMeta.label}</span>
                  </div>
                );
              })()}
              <div className="hidden md:block text-sm font-mono text-muted-foreground truncate">{user?.name}</div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/settings')}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
                title="Billing & Subscription"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                aria-label={t('logout')}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('logout')}</span>
              </Button>
            </div>
          </div>
        </header>

        {tier === 'spark' && <UpgradeBanner />}
        {tier !== 'spark' && renewal && (
          <RenewalBanner
            renewal={renewal}
            planName={renewal.planName}
            email={user?.email}
            onRenewed={() => { fetchAll(); }}
          />
        )}

        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          {/* Page heading */}
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-mono text-3xl md:text-4xl font-semibold tracking-tight mb-2">
                {t('dash_title')}
              </h2>
              <p className="text-muted-foreground">
                {plotProjects.length === 0
                  ? t('dash_noProjects')
                  : t(plotProjects.length === 1 ? 'dash_projects_one' : 'dash_projects_other', { count: plotProjects.length })}
              </p>
            </div>
            <Button
              onClick={handleCreateProject}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow transition-all hover:shadow-glow-strong"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dash_newProject')}
            </Button>
          </div>

          {/* Writing streaks + goals — retention layer */}
          <WritingStreak />

          {/* Empty state */}
          {plotProjects.length === 0 && (
            <div className="bg-card border border-border/40 p-16 text-center">
              <Layers className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              <h3 className="font-mono text-xl font-semibold mb-3">{t('dash_emptyTitle')}</h3>
              <p className="text-muted-foreground mb-6">{t('dash_emptyDesc')}</p>
              <Button
                onClick={handleCreateProject}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
              >
                {t('dash_createFirst')}
              </Button>
            </div>
          )}

          {/* Project grid */}
          {plotProjects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plotProjects.map(project => (
                <ProjectCard
                  key={project.project_id}
                  project={project}
                  editingId={editingId}
                  editTitle={editTitle}
                  setEditTitle={setEditTitle}
                  onCardClick={handleProjectClick}
                  onEdit={startEdit}
                  onDelete={handleDelete}
                  onRenameSave={handleRename}
                  onRenameCancel={() => { setEditingId(null); setEditTitle(''); }}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, project: null })}
        onConfirm={confirmDelete}
        projectName={deleteModal.project?.title || ''}
      />

      {upgradeModal && (
        <UpgradeModal
          {...upgradeModal}
          onClose={() => setUpgradeModal(null)}
        />
      )}
    </div>
  );
}

/* ─── Project Card ───────────────────────────────────────── */
function ProjectCard({
  project,
  editingId, editTitle, setEditTitle,
  onCardClick, onEdit, onDelete, onRenameSave, onRenameCancel,
}) {
  const { t } = useTranslation();
  const isEditing = editingId === project.project_id;
  const FormatIcon = FORMAT_ICONS[project.format] || Layers;
  const formatLabel = FORMAT_LABELS[project.format] || project.format;
  const lastEdited = formatDate(project.updated_at);

  return (
    <div
      onClick={() => onCardClick(project)}
      className="bg-card border border-border/40 p-6 cursor-pointer hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 flex flex-col gap-4"
    >
      {/* Top row: icon + title + actions */}
      <div className="flex items-start gap-3">
        <FormatIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
              <Input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="bg-background border-input rounded-none font-mono text-sm"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') onRenameSave(e, project.project_id); if (e.key === 'Escape') onRenameCancel(); }}
              />
              <Button size="sm" onClick={e => onRenameSave(e, project.project_id)} className="bg-primary text-primary-foreground rounded-none">Save</Button>
            </div>
          ) : (
            <p className="font-mono text-base font-semibold leading-snug truncate">{project.title}</p>
          )}
        </div>
        {!isEditing && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={e => onEdit(e, project)} className="h-7 w-7 p-0">
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={e => onDelete(e, project)} className="h-7 w-7 p-0">
              <Trash2 className="w-3.5 h-3.5 text-destructive" />
            </Button>
          </div>
        )}
      </div>

      {/* Format badge */}
      <span className="inline-flex items-center self-start border border-transparent bg-primary/20 px-2.5 py-0.5 text-xs font-mono font-semibold text-primary uppercase tracking-wide">
        {formatLabel}
      </span>

      {/* Progress */}
      <div>
        <div className="h-1.5 bg-secondary relative">
          <div
            className="absolute inset-y-0 left-0 bg-primary transition-all"
            style={{ width: `${project.progress || 0}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1 font-mono">{t('dash_progress', { pct: project.progress || 0 })}</p>
      </div>

      {/* Footer: last edited */}
      {lastEdited && (
        <p className="text-xs text-muted-foreground font-mono mt-auto pt-2 border-t border-border/20">
          {t('dash_lastEdited', { date: lastEdited })}
        </p>
      )}
    </div>
  );
}
